import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mic, Megaphone, Clock, Copy, Send, Package, Sparkles, Trophy, Target, Award, Wand2, RefreshCw } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import DashboardShell from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";

function timeGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning, everyone!";
  if (h < 17) return "Good afternoon, everyone!";
  return "Good evening, everyone!";
}

const wordCount = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;

export default function PitchAsk() {
  const { user } = useAuth();
  const [asksBoard, setAsksBoard] = useState([]);

  const [form, setForm] = useState({
    product: "",
    success_story: "",
    achievement: "",
    target: "",
    ask: "",
  });
  const [drafted, setDrafted] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);

  const greeting = timeGreeting();
  const name = user?.name || "";
  const business = user?.business || "";
  const industry = user?.industry || "";
  const chapter = user?.chapter || "";

  useEffect(() => {
    if (user) {
      setForm({
        product: user.pitch_product || "",
        success_story: user.pitch_success_story || "",
        achievement: user.pitch_achievement || "",
        target: user.pitch_target || "",
        ask: user.pitch_ask || "",
      });
      setDrafted(user.pitch_drafted || "");
    }
  }, [user]);

  useEffect(() => { api.get("/specific-asks").then(r => setAsksBoard(r.data || [])).catch(() => {}); }, []);

  const onField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const canDraft = form.product.trim() && form.ask.trim();

  const draftWithAI = async () => {
    if (!canDraft) { toast.error("Add Product / Service and Specific Ask first"); return; }
    setDrafting(true);
    try {
      const r = await api.post("/pitch/draft", form);
      setDrafted(r.data.draft || "");
      toast.success("AI draft ready · review and edit");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not draft");
    } finally { setDrafting(false); }
  };

  const savePitch = async () => {
    if (!form.product.trim()) { toast.error("Product / Service is required"); return; }
    if (!form.ask.trim()) { toast.error("Specific Ask is required"); return; }
    setSaving(true);
    try {
      await api.put("/pitch", { ...form, drafted });
      const r = await api.get("/specific-asks");
      setAsksBoard(r.data || []);
      toast.success("Pitch saved · Board refreshed");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const copyPitch = () => {
    if (!drafted.trim()) { toast.error("Draft your pitch first"); return; }
    navigator.clipboard.writeText(drafted);
    toast.success("Pitch copied to clipboard");
  };

  const role = user?.role === "admin" ? "admin" : "member";
  const wc = wordCount(drafted);
  // 140 wpm = ~105 words for 45 sec. Strict cap.
  const MAX_WORDS = 105;
  const sec = Math.min(45, Math.max(1, Math.round((wc / MAX_WORDS) * 45)));
  const overLimit = wc > MAX_WORDS;
  const nearLimit = wc > MAX_WORDS - 10 && wc <= MAX_WORDS;
  const counterClass = overLimit ? "text-rose" : nearLimit ? "text-amber-600" : "text-emerald-600";

  return (
    <DashboardShell role={role} title="Pitch & Ask" subtitle="Craft your weekly 45-second pitch and see what every member needs.">
      <div data-testid="pitch-ask-page" className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        {/* Pitch Generator */}
        <div className="bg-white rounded-2xl border border-aubergine/[0.06] p-6 xl:col-span-7">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-rose/10 text-rose flex items-center justify-center"><Mic className="w-3.5 h-3.5" /></div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-plumSoft/70">45-second Pitch · For this week&apos;s meeting</p>
          </div>

          {/* Autofetched details — read-only block */}
          <div className="rounded-xl border border-aubergine/[0.06] bg-lavender/30 p-4 mb-5">
            <div className="flex items-center gap-1.5 mb-3">
              <Sparkles className="w-3 h-3 text-plum" />
              <p className="text-[10px] uppercase tracking-widest text-plum">Auto-filled from your profile</p>
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2.5 text-sm">
              <div>
                <dt className="text-[10px] uppercase tracking-widest text-plumSoft/70">Greeting</dt>
                <dd className="text-aubergine font-medium truncate" data-testid="auto-greeting">{greeting}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-widest text-plumSoft/70">Name</dt>
                <dd className="text-aubergine font-medium truncate" data-testid="auto-name">{name || "—"}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-widest text-plumSoft/70">Business</dt>
                <dd className="text-aubergine font-medium truncate" data-testid="auto-business">{business || "—"}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-widest text-plumSoft/70">Industry</dt>
                <dd className="text-aubergine font-medium truncate" data-testid="auto-industry">{industry || "—"}</dd>
              </div>
              {chapter && (
                <div className="col-span-2 md:col-span-4">
                  <dt className="text-[10px] uppercase tracking-widest text-plumSoft/70">Chapter</dt>
                  <dd className="text-aubergine font-medium truncate">{chapter}</dd>
                </div>
              )}
            </dl>
            {(!business || !industry) && (
              <p className="mt-3 text-[11px] text-rose">
                Missing details? <Link to="/dashboard?tab=profile" className="underline">Edit your profile →</Link>
              </p>
            )}
          </div>

          {/* Five inputs */}
          <div className="space-y-4">
            <Field
              label="Product / Service"
              icon={Package}
              required
              value={form.product}
              onChange={onField("product")}
              max={160}
              placeholder="What do you offer? e.g. 4-week Founder Brand Sprint — LinkedIn + content engine."
              testid="pitch-product"
              rows={2}
            />
            <Field
              label="Success Story"
              icon={Trophy}
              hint="One recent win — use numbers where possible"
              value={form.success_story}
              onChange={onField("success_story")}
              max={280}
              placeholder="e.g. Took 12 founders to 50k+ LinkedIn followers in 90 days, generated ₹3Cr+ inbound."
              testid="pitch-success-story"
              rows={2}
            />
            <Field
              label="Key Achievement / Credential"
              icon={Award}
              hint="Credentials, awards, years of experience"
              value={form.achievement}
              onChange={onField("achievement")}
              max={200}
              placeholder="e.g. Ex-Ogilvy creative director · Forbes 30 Under 30 · 200+ founder brands shaped."
              testid="pitch-achievement"
              rows={2}
            />
            <Field
              label="Target Customer"
              icon={Target}
              hint="Who you help best"
              value={form.target}
              onChange={onField("target")}
              max={160}
              placeholder="e.g. Bootstrapped women founders ready to invest ₹50k-3L in personal brand."
              testid="pitch-target"
              rows={2}
            />
            <Field
              label="Specific Ask"
              icon={Send}
              required
              hint="The one introduction, client or opportunity you want this week"
              value={form.ask}
              onChange={onField("ask")}
              max={280}
              placeholder="e.g. Intro to D2C founders doing ₹5-15Cr ARR struggling with retention."
              testid="pitch-ask"
              rows={3}
            />
          </div>

          {/* AI Draft button */}
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <button
              onClick={draftWithAI}
              disabled={drafting || !canDraft}
              data-testid="pitch-draft-ai"
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-aubergine text-ivory text-xs uppercase tracking-widest font-semibold hover:bg-aubergineLight hover:-translate-y-0.5 transition-all shadow-luxe disabled:opacity-50 disabled:translate-y-0 disabled:bg-aubergine/40"
            >
              {drafting ? (
                <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Drafting with AI…</>
              ) : (
                <><Wand2 className="w-3.5 h-3.5" /> {drafted ? "Re-draft with AI" : "Draft with AI"}</>
              )}
            </button>
            <button
              onClick={savePitch}
              disabled={saving || !form.product.trim() || !form.ask.trim()}
              data-testid="pitch-save"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-aubergine/20 text-aubergine text-xs uppercase tracking-widest hover:bg-aubergine/[0.05] transition-colors disabled:opacity-50"
            >
              {saving ? "Saving…" : (<>Save & post my ask <Send className="w-3.5 h-3.5" /></>)}
            </button>
          </div>
        </div>

        {/* RIGHT column — AI Drafted preview + Specific Asks Board */}
        <div className="xl:col-span-5 space-y-4">
          {/* AI-drafted pitch */}
          <div className="bg-white rounded-2xl border border-aubergine/[0.06] p-6">
            <div className="flex items-center justify-between mb-4 gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-lg bg-aubergine/[0.06] text-aubergine flex items-center justify-center shrink-0"><Wand2 className="w-3.5 h-3.5" /></div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.14em] text-plumSoft/70">AI Drafted · Strictly 45 sec</p>
                  <h3 className="text-aubergine text-sm mt-0.5" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    Your professional pitch · <span className={counterClass}>{wc}/{MAX_WORDS} words · ~{sec}s</span>
                  </h3>
                </div>
              </div>
              <button onClick={copyPitch} data-testid="pitch-copy" className="text-[10px] uppercase tracking-widest text-plum hover:text-aubergine flex items-center gap-1 shrink-0">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            {drafted ? (
              <textarea
                value={drafted}
                onChange={(e) => setDrafted(e.target.value)}
                data-testid="pitch-drafted"
                rows={9}
                className="w-full p-4 rounded-xl bg-gradient-to-br from-lavender/40 to-aubergine/[0.03] border border-aubergine/[0.06] text-sm text-aubergine font-light leading-relaxed focus:outline-none focus:border-aubergine/20 resize-none"
              />
            ) : (
              <div className="p-6 rounded-xl bg-gradient-to-br from-lavender/40 to-aubergine/[0.03] border border-aubergine/[0.06] border-dashed text-center">
                <Wand2 className="w-7 h-7 mx-auto text-plumSoft/40 mb-2" />
                <p className="text-sm text-plumSoft/80 leading-relaxed">
                  Fill the form, then tap <span className="font-medium text-aubergine">&ldquo;Draft with AI&rdquo;</span> to generate a polished 45-second pitch in your voice.
                </p>
              </div>
            )}
            <p className="mt-2 text-[10px] text-plumSoft/60 flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" />
              {overLimit
                ? <span className="text-rose">Over 45 sec — trim {wc - MAX_WORDS} words.</span>
                : <>Edit freely · Save will store this version.</>
              }
            </p>
          </div>

          {/* Specific Asks Board */}
          <div className="bg-white rounded-2xl border border-aubergine/[0.06] p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-aubergine/[0.06] text-aubergine flex items-center justify-center"><Megaphone className="w-3.5 h-3.5" /></div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.14em] text-plumSoft/70">Specific Asks · Chapter Board</p>
                <h3 className="text-aubergine text-sm mt-0.5" style={{ fontFamily: '"Outfit", sans-serif' }}>Latest asks from {asksBoard.length} members</h3>
              </div>
            </div>
            {asksBoard.length === 0 ? (
              <div className="py-8 text-center text-sm text-plumSoft/80">
                <Megaphone className="w-7 h-7 mx-auto text-plumSoft/40 mb-2" />
                No asks posted yet. Be the first.
              </div>
            ) : (
              <ul className="space-y-3 max-h-[520px] overflow-y-auto pr-1" data-testid="asks-board">
                {asksBoard.map((m) => (
                  <li key={m.member_id} className="p-3.5 rounded-xl border border-aubergine/[0.06] hover:border-aubergine/15 transition-colors">
                    <div className="flex items-start gap-3">
                      <Link to={`/members/${m.member_id}`} className="shrink-0">
                        {m.avatar ? <img src={m.avatar} alt={m.name} className="w-9 h-9 rounded-full object-cover object-center" /> : <div className="w-9 h-9 rounded-full bg-aubergine/[0.08] text-aubergine flex items-center justify-center text-xs font-medium">{m.name?.[0]}</div>}
                      </Link>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
                          <Link to={`/members/${m.member_id}`} className="text-sm text-aubergine font-medium hover:text-rose">{m.name}</Link>
                          {m.category && <span className="text-[10px] uppercase tracking-widest text-plum">{m.category}</span>}
                        </div>
                        {m.business && <p className="text-[11px] text-plumSoft/70">{m.business}</p>}
                        <ul className="mt-2 space-y-1.5">
                          {m.asks.slice(0, 2).map((a, i) => (
                            <li key={i} className="flex items-start gap-2 text-[13px] text-aubergine">
                              <span className="text-rose mt-1.5 text-[8px]">●</span>
                              <span className="font-light leading-relaxed flex-1">{a.text}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}

function Field({ label, icon: Icon, hint, value, onChange, max, placeholder, testid, rows = 2, required }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[11px] uppercase tracking-widest text-plum flex items-center gap-1.5">
          <Icon className="w-3 h-3" /> {label}{required && <span className="text-rose">*</span>}
        </label>
        {hint && <span className="text-[10px] text-plumSoft/60">{hint}</span>}
      </div>
      <textarea
        rows={rows}
        value={value}
        onChange={onChange}
        data-testid={testid}
        placeholder={placeholder}
        maxLength={max}
        className="input-luxe text-sm"
      />
      <p className="text-[10px] text-plumSoft/60 mt-1 text-right">{(value || "").length}/{max}</p>
    </div>
  );
}
